<template>
    <div class="pl-4 pt-3 row nav-padding" v-loading="loading">
        <el-form class="w-full" label-width="130px" label-position="top" ref="local_presence_number_replacement"
            :model="request">

            <div class="row mb-3">
                <div class="col-12">
                    <el-alert title="How to use" type="success" :closable="false">
                        <template v-slot>
                            <ul>
                                <li>This tool only supports US and Canada LP packages</li>
                                <li>First select a company</li>
                                <li>Select an LP package</li>
                                <li>Select an operation to add or remove numbers per area code</li>
                                <li>Select the states and area codes to operate on</li>
                                <template v-if="request.operation">
                                    <li>"To max allowed" will add the maximum allowed numbers for the selected area
                                        codes. This will try to add numbers until it reaches the maximum allowed numbers
                                        or fails to find available numbers for 3 times</li>
                                    <li>If you want to customize the quantity of each area code, select the "Custom per area code" and click on the "Add quantity" button to set the quantity per each area code(s)</li>
                                    <ul>
                                        <li>You can add multiple quantities for different area codes</li>
                                        <li>You can select multiuple area codes at once for each quantity</li>
                                        <li>You can remove quantities by clicking on the "X" button</li>
                                        <li>If you don't add some of the area codes, the tool will first purchase the custom quantity ones, then will fill the package with the rest.</li>
                                    </ul>
                                </template>
                                <template v-else>
                                    <li>"To max allowed" will remove all the numbers for the selected area codes</li>
                                    <li>If you want to customize the quantity of each area code, select the "Custom per area code" and click on the "Add quantity" button to set the quantity per each area code</li>
                                    <ul>
                                        <li>You can add multiple quantities for different area codes</li>
                                        <li>You can only select one area code for each quantity</li>
                                        <li>You can remove quantities by clicking on the "X" button</li>
                                        <li>If you don't add some of the area codes, the tool will first remove the custom quantity ones, then will fully remove the rest.</li>
                                    </ul>
                                </template>
                                <li>Click on Process button to process the request</li>
                            </ul>
                        </template>
                    </el-alert>
                    <el-alert v-if="lpPackageAdditionalInfo && lpPackageAdditionalInfo.is_locked" title="Locked"
                        type="warning" :closable="false">
                        <template v-slot>
                            Current LP package is locked by another operation. Please wait for the other operation to
                            finish.
                        </template>
                    </el-alert>
                </div>
            </div>

            <div class="row">
                <div class="col-5">
                    <el-form-item label="Company" prop="company_id">
                        <company-auto-suggest placeholder="Search for the company" v-model="request.company_id"
                            @input="fetchLpLines">
                        </company-auto-suggest>
                    </el-form-item>
                </div>
                <div class="col-5">
                    <el-form-item :label="lpPackageType" prop="campaign_id">
                        <el-select class="w-full" v-model="request.campaign_id" placeholder="Select an LP package"
                            :disabled="lpLines.length === 0" filterable @change="checkTimer">
                            <el-option v-for="lpLine in lpLines" :key="lpLine.id"
                                :label="'#' + lpLine.id + ' ' + lpLine.name" :value="lpLine.id">
                            </el-option>
                        </el-select>
                    </el-form-item>
                </div>
                <div class="col-2">
                    <el-form-item label="Operation" prop="operation">
                        <el-switch style="display: block" class="mt-2" :disabled="request.campaign_id === null"
                            v-model="request.operation" active-color="#13ce66" inactive-color="#ff4949"
                            active-text="Add" inactive-text="Remove" @change="changeOperation">
                        </el-switch>
                    </el-form-item>
                </div>
            </div>

            <div class="row">
                <div class="col-6">
                    <el-form-item label="States" prop="states">
                        <el-transfer filterable :filter-method="stateFilterMethod"
                            filter-placeholder="State Abbreviations" v-model="request.states" :data="definedStates"
                            :titles="['Available', 'To operate']">
                        </el-transfer>
                    </el-form-item>
                </div>
                <div class="col-6">
                    <el-form-item label="Area codes" prop="area_codes">
                        <el-transfer filterable :filter-method="areaCodeFilterMethod" filter-placeholder="Area codes"
                            v-model="request.area_codes" :data="definedAreaCodes" :titles="['Available', 'To operate']">
                        </el-transfer>
                    </el-form-item>
                </div>
            </div>

            <div class="row">
                <div class="col-3">
                    <el-form-item label="Quantity" prop="custom_quantity">
                        <el-switch style="display: block" :disabled="request.area_codes.length === 0"
                            v-model="request.custom_quantity" placeholder="Quantity" active-text="To max allowed"
                            inactive-text="Custom per area code" @change="changeCustomQuantity">
                        </el-switch>
                    </el-form-item>
                </div>
                <div class="col-2">
                    <el-button type="success" size="small" class="w-full mt-3"
                        :disabled="request.custom_quantity || availableAreaCodes.length === 0" @click="addQuantity">
                        Add quantity
                    </el-button>
                </div>
            </div>

            <div class="row mb-2" v-for="(quantity, index) in request.quantity" :key="index">
                <div class="col-3">
                    <el-select class="w-full" v-model="quantity.area_codes" placeholder="Select at least one area code"
                        :multiple="lpAdditionalInfoKey !== 'available'" filterable
                        @change="updateMaximumQuantityToRemove(quantity.area_codes)">
                        <el-option v-for="area_code in availableAreaCodes" :key="area_code.key" :label="area_code.label"
                            :value="area_code.key">
                        </el-option>
                    </el-select>
                </div>
                <div class="col-8">
                    <el-slider v-if="lpAdditionalInfoKey !== 'available'" v-model="quantity.value" show-input :min="1"
                        :max="getMaximumQuantityToAdd">
                    </el-slider>
                    <el-slider v-else v-model="quantity.value" show-input :min="1"
                        :max="getMaximumQuantityToRemove[quantity.area_codes]">
                    </el-slider>
                </div>
                <div class="col-1">
                    <el-button type="danger" size="small" @click="removeQuantity(index)">
                        <i class="el-icon-delete"></i>
                    </el-button>
                </div>
            </div>

            <div class="row justify-content-end">
                <div class="col-3">
                    <el-form-item>
                        <el-button type="success" size="small" class="w-full mt-3" :loading="loading"
                            :disabled="disableProcessButton || loading" @click="processRequest">
                            Process
                        </el-button>
                    </el-form-item>
                </div>
            </div>
        </el-form>
    </div>
</template>

<script>
import auth from '../../auth'
import CompanyAutoSuggest from '../company-autosuggest'
import * as constants from '../../constants/local-presence-options'

export default {
    name: 'LocalPresenceTool',

    components: {
        CompanyAutoSuggest,
    },

    data() {
        return {
            loading: false,
            auth: auth,
            form_name: 'local_presence_tool',
            constants: constants,
            request: {
                company_id: null,
                campaign_id: null,
                states: [],
                area_codes: [],
                quantity: [],
                operation: true,
                custom_quantity: true,
            },
            lpLines: [],
            lpLineCancelToken: null,
            lpLinesSource: null,
            processCancelToken: null,
            processSource: null,
            lpLinesTimer: null,
            getMaximumQuantityToRemove: [],
        }
    },

    mounted() {
        this.lpLineCancelToken = axios.CancelToken;
        this.lpLinesSource = this.lpLineCancelToken.source();
        this.processCancelToken = axios.CancelToken;
        this.processSource = this.processCancelToken.source();
    },

    computed: {
        selectedLpLine() {
            if (this.request.campaign_id === null) {
                return null
            }

            return this.lpLines.find(lpLine => lpLine.id === this.request.campaign_id)
        },

        lpPackageAdditionalInfo() {
            if (this.selectedLpLine === null) {
                return null
            }

            return this.selectedLpLine.lp_package_additional_info
        },

        lpAdditionalInfoKey() {
            if (this.request.operation) {
                return 'defined'
            }

            return 'available'
        },

        maxAvailableNumbers() {
            if (this.lpPackageAdditionalInfo === null) {
                return null
            }

            // count all the available incoming numbers as defined in the lp_package_additional_info
            return Object.keys(this.lpPackageAdditionalInfo.available).reduce((acc, state) => {
                return acc + Object.keys(this.lpPackageAdditionalInfo.available[state]).reduce((acc, area_code) => {
                    return acc + this.lpPackageAdditionalInfo.available[state][area_code]
                }, 0)
            }, 0)
        },

        maxAllowedNumbers() {
            if (this.lpPackageAdditionalInfo === null) {
                return null
            }

            // count all the area codes as defined in the lp_package_additional_info
            return Object.keys(this.lpPackageAdditionalInfo.defined).reduce((acc, state) => {
                return acc + this.lpPackageAdditionalInfo.defined[state].length
            }, 0)
        },

        definedStates() {
            if (this.lpPackageAdditionalInfo === null) {
                return []
            }

            // return the states that are defined in the lp_package_additional_info
            return Object.keys(this.lpPackageAdditionalInfo[this.lpAdditionalInfoKey]).map(state => ({
                label: state,
                key: state,
            }))
        },

        definedAreaCodes() {
            if (this.lpPackageAdditionalInfo === null) {
                return []
            }

            // only add the area codes that are defined in the lp_package_additional_info.defined
            // the defined object is a map of [state => area_codes, ... ]
            // return the area codes that are in the request.states
            const selected_states = Object.keys(this.lpPackageAdditionalInfo[this.lpAdditionalInfoKey]).filter(state => this.request.states.includes(state))
            let area_codes = []
            selected_states.forEach(state => {
                let area_codes_in_state = this.lpPackageAdditionalInfo[this.lpAdditionalInfoKey][state]
                if (this.lpAdditionalInfoKey === 'available') {
                    area_codes_in_state = Object.keys(this.lpPackageAdditionalInfo[this.lpAdditionalInfoKey][state])
                }
                area_codes_in_state.forEach(area_code => {
                    let label = `[${state}] ${area_code}`
                    if (this.lpAdditionalInfoKey === 'available') {
                        label = `[${state}] ${area_code} (${this.lpPackageAdditionalInfo[this.lpAdditionalInfoKey][state][area_code]})`
                    }
                    area_codes.push({
                        label: label,
                        key: area_code,
                    })
                })
            })

            return area_codes
        },

        availableAreaCodes() {
            if (this.lpPackageAdditionalInfo === null) {
                return []
            }

            // source the area codes from selected area codes
            // if the area_code is not selected on other quantities, then add it to the availableAreaCodes
            let availableAreaCodes = this.request.area_codes
            this.request.quantity.forEach(quantity => {
                availableAreaCodes = availableAreaCodes.filter(area_code => !quantity.area_codes.includes(area_code))
            })
            // map the availableAreaCodes to have the label and key, get the label from the definedAreaCodes
            return availableAreaCodes.map(area_code => ({
                label: this.definedAreaCodes.find(definedAreaCodes => definedAreaCodes.key === area_code).label,
                key: area_code,
            }))
        },

        areaCodesWithoutCustomQuantity() {
            return this.request.area_codes.filter(area_code => !this.request.quantity.some(quantity => quantity.area_codes.includes(area_code)))
        },

        getMaximumQuantityToAdd() {
            // the maximum quantity is the minimum is maxAllowedNumbers - maxAvailableNumbers - other area codes without custom quantity
            const max_quantity = this.maxAllowedNumbers - this.maxAvailableNumbers
            return max_quantity > 0 ? max_quantity : 0
        },

        lpPackageType() {
            if (this.lpPackageAdditionalInfo === null) {
                return 'LP Package'
            }

            let afterOperationText = ''
            if (this.request.area_codes.length > 0) {
                let afterOperation = this.maxAvailableNumbers
                if (this.request.operation) {
                    afterOperation += this.request.quantity.reduce((acc, quantity) => acc + quantity.value * quantity.area_codes.length, 0)
                    if (this.areaCodesWithoutCustomQuantity.length > 0) {
                        afterOperation = this.maxAllowedNumbers
                    }
                    afterOperationText += ` [${afterOperation}] `
                }
            }

            return 'LP Package: ' + constants.LP_TYPES_LABELS[this.lpPackageAdditionalInfo.type] + ' (' + this.maxAvailableNumbers + afterOperationText + '/' + this.maxAllowedNumbers + ')'
        },

        disableProcessButton() {
            let allQuantitiesAreValid = true
            if (this.request.quantity.length > 0) {
                this.request.quantity.forEach(quantity => {
                    if (quantity.area_codes.length === 0) {
                        allQuantitiesAreValid = false
                        return
                    }
                })
            }
            return this.request.area_codes.length === 0 || !allQuantitiesAreValid || this.lpPackageAdditionalInfo.is_locked
        },
    },

    methods: {
        fetchLpLines() {
            this.lpLinesSource.cancel('fetchLpLines canceled by the user.')
            this.lpLinesSource = this.lpLineCancelToken.source()
            this.request.campaign_id = null
            this.request.states = []
            this.request.area_codes = []
            this.request.quantity = []
            this.lpLines = []

            axios.get(`/api/admin/company/${this.request.company_id}/lp-lines`, {
                cancelToken: this.lpLinesSource.token,
                params: {
                    with_additional_info: true,
                },
            })
                .then(res => {
                    this.lpLines = res.data
                })
                .catch(err => {
                    if (axios.isCancel(err)) {
                        console.log(err.message)
                    } else {
                        this.$notify.error({
                            title: 'Error searching for LP Packages',
                            message: err.response.data.message,
                        });
                    }
                })
        },

        fetchLpLine(campaign_id) {
            this.lpLinesSource.cancel('fetchLpLines canceled by the user.')
            this.lpLinesSource = this.lpLineCancelToken.source()
            axios.get(`/api/admin/company/${this.request.company_id}/lp-lines`, {
                cancelToken: this.lpLinesSource.token,
                params: {
                    with_additional_info: true,
                    campaign_id: campaign_id,
                },
            })
                .then(res => {
                    // get the first element of the returned data
                    const lpLine = res.data[0]
                    // check if the returned lp is in the lpLines array
                    const lpLineIndex = this.lpLines.findIndex(lpLine => lpLine.id === campaign_id)
                    const existingLpLine = this.lpLines[lpLineIndex]
                    if (lpLineIndex !== -1) {
                        // if the line was locked, but it's not locked anymore, clear the timer and notify the user
                        if (existingLpLine.lp_package_additional_info.is_locked && !lpLine.lp_package_additional_info.is_locked) {
                            clearInterval(this.lpLinesTimer)
                            this.$notify.success({
                                title: 'LP Package lock',
                                message: 'The LP Package is not locked anymore, you can now add or remove numbers',
                            })
                        }
                        // if the line was not locked, but it's locked now, set the timer to fetch lp lines every 5 seconds
                        if (!existingLpLine.lp_package_additional_info.is_locked && lpLine.lp_package_additional_info.is_locked) {
                            this.lpLinesTimer = setInterval(() => {
                                this.fetchLpLine(this.request.campaign_id)
                            }, 5000)
                        }
                        // update the lpLine inside the lpLines array
                        this.$set(this.lpLines, lpLineIndex, lpLine)
                    }
                })
                .catch(err => {
                    if (axios.isCancel(err)) {
                        console.log(err.message)
                    } else {
                        this.$root.handleErrors(err.response)
                    }
                })
        },

        processRequest() {
            this.loading = true
            this.processSource.cancel('processRequest canceled by the user.')
            this.processSource = this.processCancelToken.source()
            axios.post(`/api/admin/company/${this.request.company_id}/lp-lines/${this.request.campaign_id}/process`, this.request, {
                cancelToken: this.processSource.token,
            })
                .then(res => {
                    this.loading = false
                    this.fetchLpLine(this.request.campaign_id)
                    this.$notify.success({
                        title: 'Success',
                        message: res.data.message,
                    })
                })
                .catch(err => {
                    this.loading = false
                    if (axios.isCancel(err)) {
                        console.log(err.message)
                    } else {
                        this.$root.handleErrors(err.response)
                    }
                })
        },

        stateFilterMethod(query, item) {
            // search for the query in lowercase in the item label
            return item.label.toLowerCase().indexOf(query.toLowerCase()) > -1
        },

        areaCodeFilterMethod(query, item) {
            // search for the query in lowercase in the item label
            return item.label.toLowerCase().indexOf(query.toLowerCase()) > -1
        },

        addQuantity() {
            this.request.quantity.push({
                area_codes: [],
                value: 0,
            })
        },

        removeQuantity(index) {
            this.request.quantity.splice(index, 1)
        },

        changeOperation() {
            this.request.states = []
            this.request.area_codes = []
            this.request.quantity = []
            this.getMaximumQuantityToRemove = []
            this.request.custom_quantity = true
        },

        changeCustomQuantity() {
            if (!this.request.custom_quantity && this.request.quantity.length === 0) {
                this.addQuantity()
            }
            if (this.request.custom_quantity) {
                this.request.quantity = []
            }
        },

        checkTimer() {
            // Clear existing timer first
            if (this.lpLinesTimer) {
                clearInterval(this.lpLinesTimer);
            }

            if (this.lpPackageAdditionalInfo?.is_locked) {
                this.lpLinesTimer = setInterval(() => {
                    this.fetchLpLine(this.request.campaign_id);
                }, 5000);
            }
        },

        updateMaximumQuantityToRemove(selected_area_code) {
            // first walk through the lpPackageAdditionalInfo.available to get the states
            Object.keys(this.lpPackageAdditionalInfo.available).forEach(state => {
                Object.keys(this.lpPackageAdditionalInfo.available[state]).forEach(area_code => {
                    if (selected_area_code === area_code) {
                        this.getMaximumQuantityToRemove[area_code] = this.lpPackageAdditionalInfo.available[state][area_code]
                    }
                })
            })
        },
    },

    beforeDestroy() {
        // Clear the timer
        if (this.lpLinesTimer) {
            clearInterval(this.lpLinesTimer);
        }

        // Cancel any pending requests
        if (this.lpLinesSource) {
            this.lpLinesSource.cancel('Component destroyed');
        }
        if (this.processSource) {
            this.processSource.cancel('Component destroyed');
        }
    },
}
</script>
